/**
 * Serviço de Analytics com governança embutida
 *
 * Este serviço orquestra o provider registrado e adiciona:
 * - Parâmetros de contexto automáticos (env, platform, screen, schema_version)
 * - Validações de nomenclatura e alta cardinalidade
 * - Convenções de tracking
 *
 * Padrão: Provider Pattern + Dependency Inversion
 */

import type { AnalyticsProvider, EventProperties, UserProperties } from './provider';
import type { Environment, Platform, ContextParams } from './types';
import { ANALYTICS_SCHEMA_VERSION } from './types';

/**
 * Opções de inicialização do Analytics Service
 */
export interface AnalyticsServiceOptions {
  /**
   * Ambiente atual (prod/dev)
   */
  environment: Environment;

  /**
   * Plataforma
   */
  platform: Platform;

  /**
   * Tela inicial (opcional)
   */
  currentScreen?: string;

  /**
   * ID da sessão (opcional)
   */
  sessionId?: string;

  /**
   * Opções específicas do provider
   */
  providerOptions?: Record<string, any>;

  /**
   * Modo debug (loga eventos no console)
   */
  debug?: boolean;
}

/**
 * Serviço de Analytics (Singleton)
 *
 * Uso:
 * ```ts
 * // Inicializar
 * await analyticsService.initialize({
 *   environment: 'prod',
 *   platform: 'web',
 *   debug: false
 * });
 *
 * // Enviar eventos
 * analyticsService.track('button_clicked', { button_name: 'reserve_court' });
 *
 * // Track screen view
 * analyticsService.trackScreenView('home_page');
 * ```
 */
class AnalyticsService {
  private providers: AnalyticsProvider[] = [];
  private initialized = false;
  private debug = false;
  private trackingEnabled = true;
  private environment: Environment = 'prod';
  private platform: Platform = 'web';
  private currentScreen?: string;
  private sessionId?: string;
  private readonly schemaVersion = ANALYTICS_SCHEMA_VERSION;
  private contextProps: EventProperties = {};

  /**
   * Registra um provider de analytics.
   *
   * O serviço faz fan-out: cada evento/identificação é despachado para TODOS
   * os providers registrados (ex: Firebase/GA4 + Mixpanel simultaneamente).
   *
   * A deduplicação é por classe concreta — registrar o mesmo provider de novo
   * (re-import em HMR, wiring duplicado) substitui a instância anterior em vez
   * de duplicar eventos.
   *
   * @param provider - Provider que implementa AnalyticsProvider
   */
  registerProvider(provider: AnalyticsProvider): void {
    const index = this.providers.findIndex(
      (existing) => existing.constructor === provider.constructor
    );
    if (index >= 0) {
      this.providers[index] = provider;
    } else {
      this.providers.push(provider);
    }
  }

  /**
   * Inicializa o serviço de analytics
   * @param options - Opções de inicialização
   */
  async initialize(options: AnalyticsServiceOptions): Promise<void> {
    if (this.initialized) {
      console.warn('[ANALYTICS] Service already initialized');
      return;
    }

    this.debug = options.debug ?? false;
    this.environment = options.environment;
    this.platform = options.platform;
    this.currentScreen = options.currentScreen;
    this.sessionId = options.sessionId;

    if (this.providers.length > 0) {
      await Promise.all(
        this.providers.map((provider) => provider.initialize(options.providerOptions))
      );
      if (this.debug) {
        const names = this.providers.map((provider) => provider.constructor.name).join(', ');
        console.log(`[ANALYTICS] Providers initialized: ${names}`);
      }
    } else {
      console.warn('[ANALYTICS] No provider registered, analytics will not track events');
    }

    this.initialized = true;
  }

  /**
   * Obtém os parâmetros de contexto padrão
   */
  private getContextParams(): ContextParams {
    return {
      env: this.environment,
      platform: this.platform,
      schema_version: this.schemaVersion,
      ...(this.currentScreen && { screen: this.currentScreen }),
      ...(this.sessionId && { session_id: this.sessionId })
    };
  }

  /**
   * Valida nome do evento (convenções)
   */
  private validateEventName(eventName: string): void {
    // Verifica se está em lowercase_with_underscores
    if (!/^[a-z][a-z0-9_]*$/.test(eventName)) {
      console.warn(
        `[ANALYTICS] Event name '${eventName}' doesn't follow convention (lowercase_with_underscores)`
      );
    }

    // Verifica se usa verbos genéricos
    const genericTerms = ['click', 'event', 'action', 'button'];
    if (genericTerms.includes(eventName)) {
      console.warn(
        `[ANALYTICS] Event name '${eventName}' is too generic. Use descriptive names.`
      );
    }
  }

  /**
   * Valida parâmetros para prevenir alta cardinalidade
   */
  private validateParams(eventName: string, params: Record<string, any>): void {
    const warnings: string[] = [];

    // Verifica se há valores muito longos (possível texto livre)
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value === 'string' && value.length > 100) {
        warnings.push(
          `Parameter '${key}' is too long (${value.length} chars). This may cause high cardinality.`
        );
      }
    });

    // Verifica número de parâmetros
    const paramCount = Object.keys(params).length;
    if (paramCount > 15) {
      warnings.push(
        `Event '${eventName}' has ${paramCount} parameters. Consider reducing to prevent high cardinality.`
      );
    }

    // Loga avisos no console
    if (warnings.length > 0) {
      console.warn(`[ANALYTICS] Event '${eventName}' validation warnings:`, warnings);
    }
  }

  /**
   * Envia um evento de analytics com governança
   *
   * Este método:
   * - Adiciona parâmetros de contexto automaticamente
   * - Valida nomenclatura (em dev)
   * - Valida alta cardinalidade (em dev)
   *
   * @param eventName - Nome do evento (lowercase_with_underscores)
   * @param params - Parâmetros do evento
   */
  track(eventName: string, params: Record<string, any> = {}): void {
    // Opt-out gate (LGPD): when tracking is disabled, dispatch nothing.
    if (!this.trackingEnabled) {
      return;
    }

    // Validação (apenas em dev)
    if (this.environment === 'dev') {
      this.validateEventName(eventName);
      this.validateParams(eventName, params);
    }

    // Merge parâmetros do evento: contexto base + contexto persistente + params
    const fullParams = {
      ...this.getContextParams(),
      ...this.contextProps,
      ...params
    };

    if (this.debug) {
      console.log('[ANALYTICS] Event:', eventName, fullParams);
    }

    this.providers.forEach((provider) => provider.logEvent(eventName, fullParams));
  }

  /**
   * Helper: Envia evento de visualização de tela
   *
   * @param screenName - Nome da tela
   */
  trackScreenView(screenName: string): void {
    this.setCurrentScreen(screenName);
    this.track('screen_view', { screen_name: screenName });
  }

  /**
   * Define a tela/página atual
   * @param screenName - Nome da tela
   */
  setCurrentScreen(screenName: string): void {
    if (!this.trackingEnabled) {
      return;
    }

    this.currentScreen = screenName;

    if (this.debug) {
      console.log('[ANALYTICS] Set current screen:', screenName);
    }

    this.providers.forEach((provider) => provider.setCurrentScreen(screenName));
  }

  /**
   * Define propriedades de contexto persistentes, anexadas a TODO evento
   * subsequente (merge; chamadas seguintes sobrescrevem as mesmas chaves).
   *
   * Agnóstico de domínio: o app decide as chaves (ex: venue_id, tenant_id). Não
   * despacha nada sozinho — os valores viajam nos params de cada evento.
   */
  setContext(props: EventProperties): void {
    this.contextProps = { ...this.contextProps, ...props };

    if (this.debug) {
      console.log('[ANALYTICS] Set context:', this.contextProps);
    }
  }

  /**
   * Limpa todo o contexto persistente definido via setContext (ex: no logout).
   */
  clearContext(): void {
    this.contextProps = {};

    if (this.debug) {
      console.log('[ANALYTICS] Cleared context');
    }
  }

  /**
   * Define o ID da sessão
   * @param sessionId - ID da sessão
   */
  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;

    if (this.debug) {
      console.log('[ANALYTICS] Set session ID:', sessionId);
    }
  }

  /**
   * Loga um evento customizado (método legado, use track())
   * @deprecated Use track() instead
   * @param eventName - Nome do evento
   * @param properties - Propriedades do evento
   */
  logEvent(eventName: string, properties?: EventProperties): void {
    this.track(eventName, properties || {});
  }

  /**
   * Define o ID do usuário
   * @param userId - ID do usuário (ou null para limpar)
   */
  setUserId(userId: string | null): void {
    if (!this.trackingEnabled) {
      return;
    }

    if (this.debug) {
      console.log('[ANALYTICS] Set user ID:', userId);
    }

    this.providers.forEach((provider) => provider.setUserId(userId));
  }

  /**
   * Define propriedades do usuário
   * @param properties - Propriedades do usuário
   */
  setUserProperties(properties: UserProperties): void {
    if (!this.trackingEnabled) {
      return;
    }

    if (this.debug) {
      console.log('[ANALYTICS] Set user properties:', properties);
    }

    this.providers.forEach((provider) => provider.setUserProperties(properties));
  }

  /**
   * Loga visualização de página
   * @param pageName - Nome da página
   * @param pageLocation - URL da página
   */
  logPageView(pageName: string, pageLocation?: string): void {
    this.setCurrentScreen(pageName);
    this.track('screen_view', {
      screen_name: pageName,
      ...(pageLocation && { page_location: pageLocation })
    });
  }

  /**
   * Habilita ou desabilita o tracking (opt-out de LGPD).
   *
   * Quando desabilitado, nenhum evento/identificação é despachado ao provider.
   * Também propaga o consentimento ao provider (Consent Mode), se suportado.
   */
  setTrackingEnabled(enabled: boolean): void {
    this.trackingEnabled = enabled;

    if (this.debug) {
      console.log('[ANALYTICS] Tracking enabled:', enabled);
    }

    this.providers.forEach((provider) => provider.setConsent?.(enabled));
  }

  /**
   * Indica se o tracking está habilitado.
   */
  getTrackingEnabled(): boolean {
    return this.trackingEnabled;
  }

  /**
   * Verifica se o serviço foi inicializado
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Verifica se há ao menos um provider registrado
   */
  hasProvider(): boolean {
    return this.providers.length > 0;
  }
}

// Singleton instance
export const analyticsService = new AnalyticsService();
